const steps = [
  {
    num: '01',
    title: 'Free Consultation',
    desc: "We visit your property, evaluate the terrain, and listen carefully to your goals. Zero pressure — honest advice from a seasoned local expert who's installed hundreds of fences across Camden County.",
    icon: 'fa-comments',
    image: 'https://public.readdy.ai/ai/img_res/197aef6e0f52b5581f359c39170dc113.jpg',
  },
  {
    num: '02',
    title: 'Design & Material Selection',
    desc: 'Choose your fence style, material, and height with expert guidance. We walk you through every option — balancing security, privacy, aesthetics, and budget until the solution is exactly right.',
    icon: 'fa-compass-drafting',
    image: 'https://readdy.ai/api/search-image?query=Close-up%20of%20professional%20fence%20contractor%20hands%20holding%20material%20samples%20showing%20wood%20grain%20cedar%20boards%20vinyl%20panels%20aluminum%20fence%20samples%20side%20by%20side%2C%20clean%20white%20background%2C%20product%20comparison%2C%20professional%20photography%2C%20detail%20shot%2C%20sharp%20focus%20on%20materials&width=700&height=500&seq=mf_v3_proc2&orientation=landscape',
  },
  {
    num: '03',
    title: 'Expert Installation',
    desc: 'Our skilled installation crew handles everything from precision post-setting to finishing touches — working efficiently and respectfully with minimal disruption to your landscape or daily routine.',
    icon: 'fa-hammer',
    image: 'https://readdy.ai/api/search-image?query=Professional%20fencing%20crew%20installing%20premium%20cedar%20wood%20boards%20on%20a%20new%20fence%20line%20in%20bright%20sunlit%20backyard%2C%20workers%20in%20safety%20gear%20using%20power%20tools%2C%20coordinated%20team%20effort%2C%20upscale%20residential%20Georgia%20home%20visible%2C%20sawdust%20in%20the%20air%2C%20blue%20sky%2C%20authentic%20documentary%20construction%20photography%2C%20warm%20tone&width=700&height=500&seq=mf_v3_proc3&orientation=landscape',
  },
  {
    num: '04',
    title: 'Final Walkthrough',
    desc: "We inspect every post, board, and gate together before we leave — ensuring every detail is exactly as agreed. If it's not perfect, we make it right. That's the Masters promise.",
    icon: 'fa-circle-check',
    image: 'https://readdy.ai/api/search-image?query=Satisfied%20homeowner%20and%20contractor%20shaking%20hands%20in%20front%20of%20beautiful%20newly%20completed%20cedar%20wood%20privacy%20fence%2C%20both%20smiling%20proud%20with%20finished%20fence%20in%20background%2C%20warm%20golden%20afternoon%20light%2C%20upscale%20Georgia%20backyard%2C%20professional%20genuine%20moment%2C%20authentic%20photography&width=700&height=500&seq=mf_v3_proc4&orientation=landscape',
  },
];

const ProcessSection: React.FC = () => (
  <section className="relative bg-[#FAF8F5] overflow-hidden">
    <div className="section-number right-6 top-8">02</div>

    <div className="section-py max-w-7xl mx-auto px-6 relative z-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-20 gap-6 reveal">
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span className="gold-bar" />
            <span className="text-[#C4A84F] text-[11px] tracking-[0.38em] uppercase font-sans font-medium">
              How It Works
            </span>
          </div>
          <h2 className="font-serif text-[3.2rem] md:text-[4rem] lg:text-[4.8rem] text-[#0D0C0B] leading-[0.95]">
            Our Refined<br />4-Step Process
          </h2>
        </div>
        <p className="text-[#0D0C0B]/48 text-base leading-relaxed max-w-sm font-sans md:pb-2">
          From your first call to the final handshake — a seamless, professional experience designed entirely around you.
        </p>
      </div>

      {/* Steps — alternating layout */}
      <div className="space-y-0">
        {steps.map((step, idx) => (
          <div
            key={step.num}
            className={`reveal grid grid-cols-1 lg:grid-cols-2 overflow-hidden border border-[#0D0C0B]/6 mb-4 last:mb-0 group`}
          >
            {/* Image */}
            <div className={`relative h-64 lg:h-auto min-h-[320px] overflow-hidden img-zoom ${idx % 2 !== 0 ? 'lg:order-2' : ''}`}>
              <img
                src={step.image}
                alt={`${step.title} — Masters Fencing process step ${step.num}`}
                className="w-full h-full object-cover object-center"
              />
              {/* Step number overlay */}
              <div className="absolute top-5 left-5 font-serif text-7xl text-white/15 leading-none select-none">
                {step.num}
              </div>
              <div className={`absolute inset-0 bg-gradient-to-${idx % 2 === 0 ? 'r' : 'l'} from-transparent to-[#0D0C0B]/25`} />
            </div>

            {/* Content */}
            <div className={`bg-white p-10 lg:p-14 flex flex-col justify-center ${idx % 2 !== 0 ? 'lg:order-1' : ''}`}>
              <div className="flex items-center gap-4 mb-7">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C4A84F]/10 border border-[#C4A84F]/25 group-hover:bg-[#C4A84F] group-hover:border-[#C4A84F] transition-all duration-400">
                  <i className={`fa-solid ${step.icon} text-[#C4A84F] group-hover:text-[#0D0C0B] transition-colors duration-400`} />
                </div>
                <div className="text-[11px] text-[#C4A84F] tracking-[0.32em] uppercase font-sans font-medium">
                  Step {step.num}
                </div>
              </div>

              <h3 className="font-serif text-[2.2rem] md:text-[2.8rem] text-[#0D0C0B] mb-5 leading-tight">
                {step.title}
              </h3>
              <p className="text-[#0D0C0B]/52 text-sm leading-relaxed font-sans max-w-md">
                {step.desc}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Mid-CTA */}
      <div className="mt-16 bg-[#0D0C0B] p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-10 reveal overflow-hidden relative">
        <div
          className="absolute inset-0 opacity-[0.06] bg-cover bg-center pointer-events-none"
          style={{
            backgroundImage: "url('https://readdy.ai/api/search-image?query=abstract%20texture%20pattern%20of%20wood%20grain%20fence%20boards%2C%20dark%20moody%20tones%2C%20artistic%20macro%20photography&width=1400&height=400&seq=mf_v3_proc_bg&orientation=landscape')",
          }}
        />
        <div className="relative z-10">
          <p className="font-serif text-[1.8rem] md:text-[2.4rem] text-[#F5F1E8] leading-snug mb-2">
            Ready to start your project?
          </p>
          <p className="text-[#F5F1E8]/42 text-sm font-sans">
            Book your free consultation — $100 off when you sign same day.
          </p>
        </div>
        <div className="relative z-10 flex flex-col sm:flex-row gap-4 shrink-0">
          <a href="#estimate" className="btn-gold">
            <i className="fa-regular fa-calendar-check text-[11px]" />
            Request a Consultation
          </a>
          <a href="tel:912-409-2031" className="btn-outline-light">
            <i className="fa-solid fa-phone text-[11px]" />
            912-409-2031
          </a>
        </div>
      </div>
    </div>
  </section>
);

export default ProcessSection;
